<template>
  <div class="create-global-account">
    <div class="section-header">
      <div class="section-header__left">
        <div class="section-header__icon">
          <i class="el-icon-s-custom"></i>
        </div>
        <div>
          <h2 class="section-header__title">{{ $t('settings.createGlobalAccount') }}</h2>
          <p class="section-header__subtitle">{{ $t('settings.accountSection') }}</p>
        </div>
      </div>

      <div class="section-header__actions">
        <base-button
          icon="el-icon-back"
          @click="$router.push({path: '/app/global/accounts'})">
          {{ $t('settings.backToAccounts') }}
        </base-button>
      </div>
    </div>

    <el-form
      class="account-form"
      ref="form"
      :rules="rules"
      :model="account"
      label-width="160px">

      <div class="form-card">
        <div class="form-card__header">
          <div class="form-card__icon">
            <i class="el-icon-user"></i>
          </div>
          <h3 class="form-card__title">{{ $t('settings.accountSection') }}</h3>
        </div>
        <div class="form-card__body">
          <el-form-item :label="$t('base.firstName')" prop="firstName">
            <el-input v-model="account.firstName"></el-input>
          </el-form-item>
          <el-form-item :label="$t('base.lastName')" prop="lastName">
            <el-input v-model="account.lastName"></el-input>
          </el-form-item>
          <el-form-item :label="$t('base.email')" prop="email">
            <el-input v-model="account.email"></el-input>
          </el-form-item>
          <el-form-item :label="$t('base.appLoginName')" prop="appLoginName">
            <el-input v-model="account.appLoginName"></el-input>
          </el-form-item>
        </div>
      </div>

      <div class="form-card">
        <div class="form-card__header">
          <div class="form-card__icon">
            <i class="el-icon-key"></i>
          </div>
          <h3 class="form-card__title">{{ $t('settings.accessSection') }}</h3>
        </div>
        <div class="form-card__body">
          <el-form-item :label="$t('settings.authority')">
            <el-select v-model="account.userAuthority" :placeholder="$t('settings.authority')">
              <el-option
                v-for="authority in authorities"
                :label="authority"
                :key="authority"
                :value="authority">
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('settings.tenantName')">
            <el-select
              :disabled="account.userAuthority === 'SUPER_ADMIN' || !account.userAuthority"
              v-model="selectedTenantId"
              filterable
              clearable
              @change="onTenantChange"
              :placeholder="$t('settings.selectTenant')">
              <el-option
                v-for="tenant in tenants"
                :key="tenant.id"
                :label="tenant.name"
                :value="tenant.id">
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('settings.customerName')" v-if="selectedTenantId">
            <el-select
              :disabled="account.userAuthority === 'SUPER_ADMIN' || !account.userAuthority"
              v-model="account.customerId"
              filterable
              clearable
              :placeholder="$t('settings.selectCustomer')">
              <el-option
                v-for="customer in tenantCustomers"
                :key="customer.id"
                :label="customer.name"
                :value="customer.id">
              </el-option>
            </el-select>
          </el-form-item>
        </div>
      </div>

      <div class="form-actions">
        <base-button @click="onCancel">
          {{ $t('base.cancel') }}
        </base-button>
        <base-button
          type="primary"
          :loading="loading"
          @click="onSubmit">
          {{ $t('settings.create') }}
        </base-button>
      </div>
    </el-form>
  </div>
</template>

<script>
import BaseButton from '@/components/common/BaseButton';
import {adminAccountService} from '@/services/AdminAccountService.class';
import {tenantService} from '@/services/TenantService.class';

export default {
  name: "CreateGlobalAccount",
  components: {BaseButton},
  data() {
    return {
      account: {
        userAuthority: 'ADMIN',
        active: true,
        customerId: null
      },
      tenants: [],
      tenantCustomers: [],
      selectedTenantId: null,
      loading: false,
      authorities: ['SUPER_ADMIN', 'ADMIN', 'REPORTER', 'USER'],
      rules: {
        firstName: [
          {required: true, message: this.$t('settings.firstNameRequired'), trigger: 'blur'},
          {
            validator: this.validateNoSpecialChars,
            message: this.$t('settings.accountRules.noSpecialChars'),
            trigger: 'blur'
          }
        ],
        lastName: [
          {required: true, message: this.$t('settings.lastNameRequired'), trigger: 'blur'},
          {
            validator: this.validateNoSpecialChars,
            message: this.$t('settings.accountRules.noSpecialChars'),
            trigger: 'blur'
          }
        ],
        email: [
          {required: true, message: this.$t('settings.emailRequired'), trigger: 'blur'},
          {type: 'email', message: this.$t('settings.invalidEmail'), trigger: 'blur'}
        ],
        appLoginName: [
          {required: true, message: this.$t('settings.appLoginNameRequired'), trigger: 'blur'}
        ]
      }
    };
  },
  mounted() {
    this.loadTenants();
  },
  methods: {
    validateNoSpecialChars(rule, value, callback) {
      if (!value) {
        callback();
        return;
      }
      // Allow only letters, numbers, spaces, hyphens, and apostrophes
      const specialCharPattern = /[^a-zA-Z0-9\s\-']/;
      if (specialCharPattern.test(value)) {
        callback(new Error('Special characters are not allowed'));
        return;
      }
      callback();
    },
    loadTenants() {
      this.loading = true;
      tenantService.get()
        .then(response => {
          this.tenants = response.data;
        })
        .catch(error => {
          console.error('Error loading tenants:', error);
          this.$message.error(this.$t('settings.genericHttpError'));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onTenantChange(tenantId) {
      this.account.customerId = null;
      this.tenantCustomers = [];

      if (!tenantId) return;

      this.loading = true;
      tenantService.getCustomersByTenant(tenantId)
        .then(response => {
          this.tenantCustomers = response.data;
        })
        .catch(error => {
          console.error('Error loading customers:', error);
          this.$message.error(this.$t('settings.genericHttpError'));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onSubmit() {
      this.$refs['form'].validate((valid) => {
        if (valid) {
          if (!this.account.customerId && this.account.userAuthority !== "SUPER_ADMIN") {
            this.$message.warning(this.$t('settings.customer.required'));
            return;
          }

          this.loading = true;

          adminAccountService.createAccount(this.account)
            .then(() => {
              this.$message.success(this.$t('settings.accountCreated'));
              this.$router.push({path: '/app/global/accounts'});
            })
            .catch(error => {
              console.error('Error creating account:', error);
              this.$message.error(this.$t('settings.genericHttpError'));
            })
            .finally(() => {
              this.loading = false;
            });
        }
      });
    },
    onCancel() {
      this.$router.push({path: '/app/global/accounts'});
    }
  }
}
</script>

<style scoped lang="scss">
.create-global-account {
  padding: 0 15px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;

  &__left {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  &__icon {
    width: 42px;
    height: 42px;
    border-radius: 10px;
    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      color: var(--bg-surface);
      font-size: 18px;
    }
  }

  &__title {
    margin: 0 0 2px;
    font-size: 17px;
    font-weight: 600;
    color: var(--text-primary);
    line-height: 1.3;
  }

  &__subtitle {
    margin: 0;
    font-size: 12px;
    color: var(--text-secondary);
    line-height: 1.4;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 10px;
  }
}

.account-form {
  width: 100%;
}

.form-card {
  background: var(--bg-surface);
  border-radius: 12px;
  border: 1px solid var(--border);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  margin-bottom: 20px;

  &__header {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 16px 20px;
    border-bottom: 1px solid var(--border);
  }

  &__icon {
    width: 36px;
    height: 36px;
    border-radius: 9px;
    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      color: var(--bg-surface);
      font-size: 16px;
    }
  }

  &__title {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: var(--text-primary);
    line-height: 1.3;
  }

  &__body {
    padding: 24px 20px 4px;

    .el-select {
      width: 100%;
    }
  }
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-bottom: 20px;
}

@media (max-width: 767px) {
  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
  }

  .form-card__body {
    padding: 20px 14px 4px;
  }
}
</style>
